import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useAllowedFloatingUpsellPath } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useAllowedFloatingUpsellPath.js';
import { isUpsellDismissed, useDismissedUpsellsStore, } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useDismissedUpsellsStore.js';
import { isComputerRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { getHomepageUpsellsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/upsellKeys.js';
import { getHomepageUpsells } from '#pplx/frontend/apps/ask/queries/upsell-queries/getHomepageUpsells.js';
import { isCometLandingPage } from '#pplx/frontend/apps/ask/routing/routes.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { keepPreviousData } from '@tanstack/react-query';
export const useHomepageUpsellsQuery = ({ enabled = true, location: locationOverride, } = {}) => {
    const { session } = useSession();
    const selectedSearchMode = useSelectedSearchMode();
    const pathname = usePathname();
    // The Computer surface always operates in ASI mode, but the shared
    // ThreadSettingsProvider's configuredModel (which useSelectedSearchMode reads)
    // only resolves to an ASI model when the user's homepage default is Computer.
    // On a Computer thread/route for everyone else it stays a Search model, so
    // homepage upsells would be requested in Search mode and ASI-gated upsells
    // (e.g. the advanced-preview input banner) would leak onto the Computer
    // surface. Pin ASI on Computer routes so the backend receives the correct
    // X-Search-Mode and its ASI gating applies.
    const searchMode = isComputerRoute(pathname) ? SearchMode.ASI : selectedSearchMode;
    const isHomepage = useAllowedFloatingUpsellPath() || Boolean(isCometLandingPage(pathname));
    const location = locationOverride ?? (isHomepage ? 'homepage' : undefined);
    const userId = session?.user?.id ?? null;
    const dismissedUserId = useDismissedUpsellsStore((s) => s.userId);
    const dismissedNames = useDismissedUpsellsStore((s) => s.dismissedNames);
    const { data, isLoading, isError } = useQuery({
        enabled,
        // Cache homepage upsells per auth/subscription context so a logged-out or
        // pre-upgrade response cannot survive into an enterprise/pro session.
        queryKey: getHomepageUpsellsQueryKey({
            userId,
            subscriptionTier: session?.user?.subscription_tier ?? null,
            subscriptionStatus: session?.user?.subscription_status ?? null,
            organizationUuid: session?.user?.org_uuid ?? null,
            orgRole: session?.user?.org_role ?? null,
            searchMode,
            location,
        }),
        queryFn: () => getHomepageUpsells(searchMode, { location }),
        // Cache for the duration of the session (no staleTime means it won't refetch until page refresh)
        staleTime: Infinity,
        gcTime: Infinity,
        // Keep the previous mode's upsell data while the new mode's query is in
        // flight. Without this, switching modes briefly drops `productBanner` to
        // null, which collapses the bottom-card upsell and force-expands the
        // primers tab before snapping back — a visible flash.
        placeholderData: keepPreviousData,
    });
    const dismissState = { userId: dismissedUserId, dismissedNames };
    const filterDismissed = (info) => info && isUpsellDismissed(info.name, userId, dismissState) ? null : info;
    return {
        upsellInformation: filterDismissed(data?.upsell_information ?? null),
        productBanner: filterDismissed(data?.product_banner_information ?? null),
        inputUpsellInformation: filterDismissed(data?.input_upsell_information ?? null),
        isLoading,
        isError,
    };
};
//# sourceMappingURL=useHomepageUpsellsQuery.js.map